import React, { useState, useEffect } from 'react';
import { 
  IconButton, 
  TextField, 
  CircularProgress,
  Typography,
  Box
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import api from '../axios/api';
import Button from '../components/common/styled components/Button';
import Grid from '../components/common/Grid';

const ActivityPoints = () => {
  const [activityData, setActivityData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPoints();
  }, []);

  const fetchPoints = async () => {
    try {
      const response = await api.get('/api/admin/getPoints');
      if (response.status !== 200) {
        throw new Error('Failed to fetch points');
      }
      setActivityData(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    setEditingId(id);
  };

  const handlePointsChange = (id, newPoints) => {
    setActivityData(activityData.map(item => 
      item.id === id ? { ...item, points: newPoints } : item
    ));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/admin/updatePoints', activityData);

      if (response.status !== 200) {
        throw new Error('Failed to update points');
      }

      // Refresh the data after successful update
      await fetchPoints();
      setEditingId(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }

  const columns = [
    {
      header: "Activity Code",
      accessor: "activity_code",
      sortable: true,
      align: "left",
    },
    {
      header: "Points",
      accessor: "points",
      sortable: true,
      align: "center",
      render: (val, row) =>
        editingId === row.id ? (
          <TextField
            value={row.points}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || (/^\d*$/.test(value) && !value.includes('.'))) {
                handlePointsChange(row.id, value);
              }
            }}
            type="number"
            size="small"
            autoFocus
          />
        ) : (
          val
        ),
    },
    {
      header: "Updated Date",
      accessor: "updated_date",
      sortable: true,
      align: "center",
      render: (val) => val || "-",
    },
    {
      header: "Updated By",
      accessor: "updated_by",
      sortable: true,
      align: "center",
      render: (val) => val || "-",
    },
    {
      header: "Actions",
      align: "center",
      render: (_, row) => (
        <IconButton onClick={() => handleEdit(row.id)} size="small" title="Edit points">
          <EditIcon color="primary" fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box className="p-4">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 4,
            height: 28,
            bgcolor: "#10b981",
            borderRadius: "4px",
            flexShrink: 0,
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: "1.4rem", sm: "1.75rem" },
            fontWeight: 700,
            color: "#002F43",
            letterSpacing: "-0.02em",
            m: 0,
            lineHeight: 1.2,
          }}
        >
          Reward Points Setting
        </Typography>
      </Box>

      <Grid
        columns={columns}
        data={activityData}
        loading={loading}
        variant="navy"
        rowKey="id"
      />

      <Box className="mt-4 flex justify-end" sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button 
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </Box>      
    </Box>
  );
};

export default ActivityPoints;